import { ReactNode } from "react";

import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import { Autocomplete, Checkbox, Chip, TextField } from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";

interface Props<T> {
  label: string;
  options: T[];
  value: T[];
  onChange: (value: T[]) => void;
  getOptionLabel: (option: T) => string;
  // A React key for both the option row and its chip — identity for primitive options, an id field
  // for object ones. Kept explicit rather than derived from getOptionLabel: two options can share a
  // label (a renamed or duplicate entry) without sharing an identity.
  getOptionKey: (option: T) => string | number;
  // Object options need this — MUI otherwise falls back to `===`, which never matches two distinct
  // instances describing the same thing. Leave unset for primitive options, where `===` is exactly
  // right.
  isOptionEqualToValue?: (option: T, value: T) => boolean;
  // What a chip shows, when that needs to differ from the option row's label — e.g. a short code in
  // the chip next to the fuller "code (name)" in the dropdown. Defaults to getOptionLabel.
  getChipLabel?: (option: T) => string;
  // Beyond this many, the rest collapse into one "+N" chip — always, not only while unfocused like
  // MUI's own `limitTags`, so reopening the dropdown to add another option cannot re-expand the row
  // and grow whatever sits below it. The dropdown's checkmarks still show the full selection.
  maxVisibleChips?: number;
  placeholder?: string;
  helperText?: ReactNode;
  error?: boolean;
  required?: boolean;
  fullWidth?: boolean;
  sx?: SxProps<Theme>;
}

/**
 * A multi-select with a checkbox per option and the current selection shown as chips in the field —
 * the shape shared by every "pick several of these" field in the app (Bezirke, Gerichtstage, the
 * Dienstplan Berater:innen filter). A thin `Autocomplete` wrapper rather than a new control: the
 * dropdown, keyboard handling and typeahead are all MUI's own.
 */
export function CheckboxMultiSelect<T>({
  label,
  options,
  value,
  onChange,
  getOptionLabel,
  getOptionKey,
  isOptionEqualToValue,
  getChipLabel,
  maxVisibleChips,
  placeholder,
  helperText,
  error,
  required,
  fullWidth,
  sx,
}: Props<T>) {
  const chipLabelFor = getChipLabel ?? getOptionLabel;

  return (
    <Autocomplete<T, true>
      multiple
      disableCloseOnSelect
      options={options}
      value={value}
      onChange={(_, newValue) => onChange(newValue)}
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={isOptionEqualToValue}
      fullWidth={fullWidth}
      sx={sx}
      renderValue={(selected, getItemProps) => {
        const visible = maxVisibleChips == null ? selected : selected.slice(0, maxVisibleChips);
        const hiddenCount = selected.length - visible.length;

        return [
          ...visible.map((option, index) => (
            <Chip
              {...getItemProps({ index })}
              key={getOptionKey(option)}
              size="small"
              color="primary"
              variant="outlined"
              label={chipLabelFor(option)}
            />
          )),
          hiddenCount > 0 && <Chip key="__more__" size="small" variant="outlined" label={`+${hiddenCount}`} />,
        ];
      }}
      renderOption={({ key, ...optionProps }, option, { selected }) => (
        <li key={key} {...optionProps}>
          <Checkbox
            icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
            checkedIcon={<CheckBoxIcon fontSize="small" />}
            checked={selected}
            size="small"
            sx={{ mr: 1 }}
          />
          {getOptionLabel(option)}
        </li>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          required={required}
          placeholder={value.length === 0 ? placeholder : undefined}
          error={error}
          helperText={helperText}
          size="small"
        />
      )}
    />
  );
}
